// File: frontend/src/components/ui/DataTable.js
//
// The one table. Wraps MUI Table with a declarative `columns` API so the ~24
// bespoke tables across the app can share one consistent header style, row
// height, hover, loading skeleton, and empty state — all theme-driven (the head/
// cell/row styling already lives in theme.js).
//
//   <DataTable
//     columns={[
//       { key: "name",   header: "Agent",  render: (r) => r.name },
//       { key: "amount", header: "Amount", align: "right", render: (r) => fmt(r.amount) },
//       { key: "status", header: "Status", render: (r) => <StatusBadge status={r.status} /> },
//     ]}
//     rows={agents}
//     getRowKey={(r) => r.id}
//     loading={loading}
//     emptyTitle="No agents yet"
//     onRowClick={(r) => openAgent(r)}
//   />
//
// Column: { key, header, render?(row,i), align?, headerAlign?, width?, cellSx?, headerSx?, skeletonWidth? }
//
// Mobile — THE RULE: a table with more than MOBILE_CARD_COLUMN_LIMIT columns is
// never a sideways scroll on a phone. Below `sm` it becomes a card list:
//   · pass `renderMobileCard` to design the card yourself (Users, Loyalty);
//   · otherwise the card is BUILT FROM THE COLUMNS — the first column (or the
//     one marked `mobilePrimary`) as the card's title, every other column as a
//     label/value row using its own header and its own render. Nothing a desktop
//     reader can see is lost, and nothing is re-implemented per page.
// Column hints for the built card: `mobile: false` (leave this column out — an
// icon-only column that repeats the status, say), `mobilePrimary: true`,
// `mobileLabel` (a shorter label than the header).
// A table of four columns or fewer fits a phone as a table and is left alone.
// Optional companions — `mobileVariant` ("card" separated | "row" flush, for a
// list already inside a bordered surface) and `getCardAriaLabel` (the one
// sentence a screen reader should hear for a whole card).

import React from "react";
import {
  Box, Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
  useMediaQuery, useTheme,
} from "@mui/material";
import EmptyState from "./EmptyState";
import MobileCardList from "./MobileCardList";
import { SkeletonTableRows } from "./LoadingState";
import { TYPE_SCALE, MIN_TAP } from "./tokens";

// Above this, a table cannot honestly be squeezed into a phone's width — the
// columns that matter end up off-screen behind a horizontal scroll nobody
// discovers. Measured against the audit: 23 of 24 tables had no phone form.
export const MOBILE_CARD_COLUMN_LIMIT = 4;

const cellOf = (c, row, i) => (c.render ? c.render(row, i) : row[c.key]);

/**
 * The card a wide table gets on a phone when its page did not design one.
 * Structured rows, not a squeezed table: a title, then label → value.
 */
function ColumnCard({ columns, row, index }) {
  const shown = columns.filter((c) => c.mobile !== false);
  const primary = shown.find((c) => c.mobilePrimary) || shown[0];
  const rest = shown.filter((c) => c !== primary);
  return (
    // A built card only ever renders below `sm`, i.e. under a thumb, so every
    // control a column puts in it meets the product's 44px floor — the row
    // actions a desktop table draws as compact buttons are otherwise ~30px.
    <Box sx={{ minWidth: 0, "& button, & a[href], & [role='button']": { minHeight: MIN_TAP } }}>
      {primary && (
        <Box sx={{ fontWeight: 700, fontSize: TYPE_SCALE.body, minWidth: 0, overflowWrap: "anywhere", mb: rest.length ? 0.75 : 0 }}>
          {cellOf(primary, row, index)}
        </Box>
      )}
      {rest.length > 0 && (
        <Box sx={{
          display: "grid",
          gridTemplateColumns: "minmax(0, max-content) minmax(0, 1fr)",
          columnGap: 1.5, rowGap: 0.5, alignItems: "center",
        }}>
          {rest.map((c) => {
            const label = c.mobileLabel ?? c.header;
            const hasLabel = label != null && label !== "";
            return (
              <React.Fragment key={c.key}>
                {hasLabel && (
                  <Box sx={{ fontSize: TYPE_SCALE.label, fontWeight: 600, color: "text.secondary", minWidth: 0 }}>
                    {label}
                  </Box>
                )}
                {/* A column with no header (row actions, usually) spans the card
                    rather than sitting beside an empty label. */}
                <Box sx={{
                  fontSize: TYPE_SCALE.body, minWidth: 0, overflowWrap: "anywhere",
                  textAlign: hasLabel ? "right" : "left",
                  justifySelf: hasLabel ? "end" : "stretch",
                  gridColumn: hasLabel ? "auto" : "1 / -1",
                }}>
                  {cellOf(c, row, index)}
                </Box>
              </React.Fragment>
            );
          })}
        </Box>
      )}
    </Box>
  );
}

export default function DataTable({
  columns = [],
  rows = [],
  getRowKey,
  loading = false,
  dense = false,
  stickyHeader = false,
  onRowClick,
  maxHeight,
  emptyState,
  emptyTitle = "Nothing here yet",
  emptyDescription,
  renderMobileCard,
  mobileVariant = "card",
  getCardAriaLabel,
  sx,
}) {
  const colCount = columns.length;
  const showEmpty = !loading && rows.length === 0;
  const theme = useTheme();
  const isNarrow = useMediaQuery(theme.breakpoints.down("sm"));

  // ── Cards on a narrow screen ──────────────────────────────────────────────
  // A table with more columns than a phone has room for either scrolls
  // sideways — where the columns that matter are the ones off-screen — or
  // squeezes every value into an unreadable strip. Neither is a table anymore.
  // A designed card wins; a wide table without one gets the column card; a
  // narrow table stays a table.
  const customCard = typeof renderMobileCard === "function";
  const useCards = isNarrow && (customCard || colCount > MOBILE_CARD_COLUMN_LIMIT);

  // `sx` is written for the TABLE — the Super console passes minWidth 700–800 so
  // its desktop table keeps its columns. Handed to the card list unchanged, that
  // made every phone card 800px wide inside a sideways scroll: the exact defect
  // the cards exist to remove (measured: Super → Tenants at 320–430px). A card
  // list takes the width it is given.
  const { minWidth: _tableMinWidth, ...cardSx } = sx || {};

  if (useCards) {
    // Behaviour stays here, beside `onRowClick`, so a card and a row open the
    // same thing in the same way. MobileCardList draws them; it decides
    // nothing about what they do.
    const getItemProps = (row, i) => ({
      "aria-label": getCardAriaLabel ? getCardAriaLabel(row, i) : undefined,
      ...(onRowClick ? {
        // A card that opens something has to be reachable without a mouse.
        role: "button",
        tabIndex: 0,
        onClick: () => onRowClick(row, i),
        onKeyDown: (e) => {
          if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onRowClick(row, i); }
        },
      } : null),
    });

    return (
      <MobileCardList
        items={rows}
        getKey={getRowKey}
        getItemProps={getItemProps}
        renderItem={customCard ? renderMobileCard : (row, i) => <ColumnCard columns={columns} row={row} index={i} />}
        loading={loading}
        variant={mobileVariant}
        emptyState={emptyState}
        emptyTitle={emptyTitle}
        emptyDescription={emptyDescription}
        sx={cardSx}
      />
    );
  }

  return (
    <TableContainer sx={{ maxHeight, ...sx }}>
      <Table size={dense ? "small" : "medium"} stickyHeader={stickyHeader}>
        <TableHead>
          <TableRow>
            {columns.map((c) => (
              <TableCell
                key={c.key}
                align={c.headerAlign || c.align || "left"}
                sx={{ width: c.width, ...c.headerSx }}
              >
                {c.header}
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {loading && (
            <SkeletonTableRows
              rows={5}
              columns={columns.map((c) => c.skeletonWidth || 90)}
              TableRow={TableRow}
              TableCell={TableCell}
            />
          )}

          {showEmpty && (
            <TableRow>
              <TableCell colSpan={colCount} sx={{ border: 0, py: 0 }}>
                {emptyState || (
                  <EmptyState title={emptyTitle} description={emptyDescription} dense />
                )}
              </TableCell>
            </TableRow>
          )}

          {!loading && rows.map((row, i) => (
            <TableRow
              key={getRowKey ? getRowKey(row, i) : i}
              hover={Boolean(onRowClick)}
              onClick={onRowClick ? () => onRowClick(row, i) : undefined}
              sx={{ cursor: onRowClick ? "pointer" : "default" }}
            >
              {columns.map((c) => (
                <TableCell key={c.key} align={c.align || "left"} sx={c.cellSx}>
                  {c.render ? c.render(row, i) : row[c.key]}
                </TableCell>
              ))}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
}
